/**
 * PB-Detail · Kommunikation
 *
 * Zwei Bereiche:
 *   1. Nachrichten-Liste (paginated) mit "Neue Nachricht"-Formular.
 *      Nachrichten werden per POST /v1/admin/message via Queue gesendet.
 *      Da E-Mails gequeued werden, erscheinen sie bei Reload in der Liste.
 *   2. Chat-Bereich — REST-Persistenz via /v1/admin/user/{id}/messages;
 *      Live-WebSocket ist out-of-scope (klar dokumentiert).
 *
 * Batch-13 Paket E — Familienkonto-Zentrierung: hat der User ein
 * Familienkonto (`user.family_account_id`, nur PB — BK hat keines), läuft
 * der Verlauf über den Konto-weiten v2-Endpoint (familyAccountMessages —
 * /v2/admin/family-accounts/{id}/messages, alle Mitglieder, Personen-Chip,
 * Personen-Filter wie im AufgabenNotizenTab). "+ Neue Nachricht" bleibt
 * personenbezogen (E-Mail hängt an einer Adresse) — Personen-Auswahl mit
 * Default "Pflegeempfänger", Versand über POST /v2/admin/users/{id}/messages
 * (setzt account_id serverseitig automatisch aus users.family_account_id).
 * Ohne Konto (BK, oder PB ohne family_account_id) bleibt der bisherige
 * v1-Weg (v1Messages.send, /v1/admin/user/{id}/messages) unverändert.
 *
 * "+ Vorlage" (EmailVorlageModal, NICHT angefasst) läuft weiterhin über
 * POST /v2/admin/email-templates/{id}/send?user_id — der Backend-Endpoint
 * schreibt dort bereits account_id mit (EmailTemplateController::
 * recordMessageHistory(), Teil des Backend-Batches).
 *
 * Entscheidungsbedarf (gemeldet): createMessage erfordert empfaenger (E-Mail-
 * Adresse des Empfängers). Die V1User-Entität liefert user.email — falls leer,
 * wird das Formular mit einem Hinweis gesperrt.
 */
import { MentionPopover } from '../../../components/chat/MentionPopover';
import { useMentionInput } from '../../../components/chat/useMentionInput';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { formatDateTime, formatDayMonth, formatTime } from '../../../lib/dateFormat';
import { v1Users, v1Messages, v1Chat, type V1Message, type V1User, type V1Chat } from '../../../lib/v1';
import { familyAccountMessages } from '../../../lib/familyAccountData';
import { v2FamilyAccounts, type FamilyMember } from '../../../lib/familyAccounts';
import { useAuth } from '../../../contexts/AuthContext';
import { Gespraechsprotokolle } from '../../../components/calls/Gespraechsprotokolle';

// CallRow wanderte in die gemeinsame Komponente; Re-Export hält alte Importe lauffähig.
export { CallRow } from '../../../components/calls/Gespraechsprotokolle';
import { EmailVorlageModal } from './EmailVorlageModal';

interface Props {
  user: V1User | null;
  dark: boolean;
}

/** Nachrichten-Zeile — additiv um Batch-13-Paket-E-Felder (Konto-Modus). */
type MessageRowData = V1Message & { account_id?: number | null; user_name?: string | null };

/* ─── Neue-Nachricht-Formular ──────────────────────────────────────────────── */

interface SendFormProps {
  dark: boolean;
  user: V1User;
  onSent: () => void;
  onCancel: () => void;
  /** Batch-13 Paket E — Konto-Modus: Empfänger-Auswahl unter den Mitgliedern. */
  accountId?: number | null;
  members?: FamilyMember[];
}

function SendMessageForm({ dark, user, onSent, onCancel, accountId, members }: SendFormProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [betreff, setBetreff] = useState('');
  const [nachricht, setNachricht] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const betreffRef = useRef<HTMLInputElement>(null);

  useEffect(() => { betreffRef.current?.focus(); }, []);

  // Konto-Modus: nur Mitglieder mit E-Mail sind wählbar (Versand ist
  // personenbezogen — ohne Adresse kann nicht gesendet werden). Default:
  // der Pflegeempfänger des Kontos, sonst das erste Mitglied mit E-Mail.
  const sendableMembers = useMemo(
    () => (accountId != null ? (members ?? []).filter(m => !!m.email && m.user_id != null) : []),
    [accountId, members],
  );
  const defaultRecipientId = useMemo(() => {
    if (accountId == null) return null;
    const pe = sendableMembers.find(m => m.is_pflegeempfaenger);
    return (pe ?? sendableMembers[0])?.user_id ?? null;
  }, [accountId, sendableMembers]);

  const [recipientId, setRecipientId] = useState<number | null>(defaultRecipientId);
  useEffect(() => { setRecipientId(defaultRecipientId); }, [defaultRecipientId]);

  const recipientEmail = accountId != null
    ? (sendableMembers.find(m => m.user_id === recipientId)?.email ?? '')
    : (user.email ?? '');
  const canSend = !!recipientEmail;

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!betreff.trim()) { setError(tr('pflege.comm.betreff_required')); return; }
    if (!nachricht.trim()) { setError(tr('pflege.comm.nachricht_required')); return; }
    setSaving(true);
    setError(null);
    try {
      if (accountId != null && recipientId != null) {
        // Batch-13 Paket E — personenbezogener Versand über die Konto-Route;
        // account_id wird serverseitig aus users.family_account_id gesetzt.
        await familyAccountMessages.send(recipientId, {
          betreff: betreff.trim(),
          nachricht: nachricht.trim(),
          smtp: 'smtp',
        });
      } else {
        // empfaenger ist ein Empfänger-TYP ('user' = die Person selbst), keine
        // E-Mail-Adresse — mit der Adresse fand der SendMessage-Job keinen
        // Empfänger und es ging keine Mail raus (betraf jede BK).
        await v1Messages.send({
          user_id: user.id,
          empfaenger: 'user',
          empfaenger_id: user.id,
          betreff: betreff.trim(),
          nachricht: nachricht.trim(),
          smtp: 'smtp',
        });
      }
      setBetreff('');
      setNachricht('');
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('pflege.detail.dashboard.save_error'));
    } finally {
      setSaving(false);
    }
  }, [betreff, nachricht, user, recipientEmail, accountId, recipientId, tr, onSent]);

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '7px 10px',
    border: `1px solid ${t.border}`, borderRadius: 7,
    background: t.inputBg, color: t.text, outline: 'none',
    fontFamily: 'Outfit,sans-serif', width: '100%', boxSizing: 'border-box',
  };

  if (!canSend) {
    return (
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}`, fontSize: 13, color: t.muted, fontStyle: 'italic' }}>
        {tr('pflege.comm.no_email_hint')}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
        display: 'flex', flexDirection: 'column', gap: 10,
        background: t.hover,
      }}
    >
      {/* Batch-13 Paket E — Empfänger-Auswahl im Konto-Modus (Default: Pflegeempfänger). */}
      {accountId != null && sendableMembers.length > 0 && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: t.muted }}>
          {tr('pflege.tasks.person_label')}
          <select
            aria-label={tr('pflege.tasks.person_label')}
            value={recipientId ?? ''}
            onChange={e => setRecipientId(e.target.value === '' ? null : Number(e.target.value))}
            style={inputStyle}
          >
            {sendableMembers.map(m => (
              <option key={m.user_id} value={m.user_id ?? ''}>
                {(m.fullname || [m.vorname, m.nachname].filter(Boolean).join(' ')).trim() || m.email} ({m.email})
              </option>
            ))}
          </select>
        </label>
      )}
      <div style={{ fontSize: 12.5, fontWeight: 600, color: t.muted }}>
        {tr('pflege.comm.recipient_label')}: <span style={{ color: t.heading }}>{recipientEmail}</span>
      </div>
      <input
        ref={betreffRef}
        type="text"
        placeholder={tr('pflege.comm.betreff_placeholder')}
        value={betreff}
        onChange={e => setBetreff(e.target.value)}
        style={inputStyle}
      />
      <textarea
        rows={4}
        placeholder={tr('pflege.comm.nachricht_placeholder')}
        value={nachricht}
        onChange={e => setNachricht(e.target.value)}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
      {error && <div style={{ fontSize: 11.5, color: '#b42318' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={saving}
          style={{ flex: 1, fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 0', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}
        >{saving ? '…' : tr('pflege.comm.send_action')}</button>
        <button
          type="button"
          onClick={onCancel}
          style={{ fontSize: 13, padding: '8px 14px', background: 'none', border: `1px solid ${t.border}`, borderRadius: 7, color: t.muted, cursor: 'pointer' }}
        >{tr('common.action.cancel')}</button>
      </div>
      <div style={{ fontSize: 11, color: t.muted, fontStyle: 'italic' }}>
        {tr('pflege.comm.queue_note')}
      </div>
    </form>
  );
}

/* ─── Hauptkomponente ───────────────────────────────────────────────────────── */

export function KommunikationTab({ user, dark }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  // Batch-13 Paket E — Konto-Modus: nur PB mit family_account_id (BK hat keines).
  const accountId: number | null = (user as { family_account_id?: number | null } | null)?.family_account_id ?? null;

  // Mitglieder des Kontos — für die Empfänger-Auswahl im Sendeformular und
  // den Personen-Filter. Nur geladen, wenn ein Konto vorliegt.
  const [members, setMembers] = useState<FamilyMember[]>([]);
  useEffect(() => {
    if (accountId == null) { setMembers([]); return; }
    let alive = true;
    v2FamilyAccounts.get(accountId)
      .then(acc => { if (alive) setMembers(acc.members ?? []); })
      .catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, [accountId]);

  // Personen-Filter (Konto-Modus): 'all' oder eine user_id — clientseitig
  // auf die geladene Seite angewandt, kein zusätzlicher Request.
  const [personFilter, setPersonFilter] = useState<'all' | number>('all');

  const [messagesRaw, setMessagesRaw] = useState<MessageRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSendForm, setShowSendForm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const loadMessages = useCallback(() => {
    if (!user?.id) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);

    if (accountId != null) {
      familyAccountMessages.list(accountId, { per_page: 50 })
        .then(p => { if (alive) setMessagesRaw(p.data as unknown as MessageRowData[]); })
        .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('pflege.comm.error_load')); })
        .finally(() => { if (alive) setLoading(false); });
      return () => { alive = false; };
    }

    v1Users.messages(user.id, { perPage: 50 })
      .then(p => { if (alive) setMessagesRaw(p.data); })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('pflege.comm.error_load')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user?.id, accountId, tr]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Personen-Filter auf die geladene Seite anwenden (nur Konto-Modus).
  const messages = useMemo(() => {
    if (accountId == null || personFilter === 'all') return messagesRaw;
    return messagesRaw.filter(row => row.user_id === personFilter);
  }, [messagesRaw, accountId, personFilter]);

  const handleSent = useCallback(() => {
    setShowSendForm(false);
    // reload so the queued message appears after a brief delay
    setTimeout(() => loadMessages(), 1500);
  }, [loadMessages]);

  const handleTemplateSent = useCallback(() => {
    setShowTemplateModal(false);
    loadMessages();
  }, [loadMessages]);

  const canSendEmail = !!user?.email;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0 }}>

      {/* Batch-13 Paket E — Personen-Filter (nur im Konto-Modus, wenn das
          Konto mehr als nur die geöffnete Person hat). */}
      {accountId != null && members.length > 0 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: t.text }}>
          {tr('pflege.tasks.person_filter_label')}
          <select
            aria-label={tr('pflege.tasks.person_filter_label')}
            value={personFilter}
            onChange={e => setPersonFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            style={{ fontSize: 12.5, padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 7, background: t.inputBg, color: t.text }}
          >
            <option value="all">{tr('pflege.tasks.person_filter_all')}</option>
            {members.filter(m => m.user_id != null).map(m => (
              <option key={m.user_id} value={m.user_id ?? ''}>
                {(m.fullname || [m.vorname, m.nachname].filter(Boolean).join(' ')).trim() || `#${m.user_id}`}
              </option>
            ))}
          </select>
        </label>
      )}

      <Card dark={dark} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        {/* header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>
            {tr('email.thread_count', { count: messages.length })}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {loading && <span style={{ fontSize: 12, color: t.muted }}>{tr('common.loading_short')}</span>}
            <button
              onClick={() => setShowSendForm(v => !v)}
              style={{ fontSize: 12, fontWeight: 600, background: showSendForm ? t.hover : BRAND_LIGHT, color: BRAND, border: `1px solid ${BRAND}`, borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}
            >{showSendForm ? tr('common.action.cancel') : `+ ${tr('pflege.comm.new_message')}`}</button>
            <button
              onClick={() => setShowTemplateModal(true)}
              disabled={!canSendEmail}
              title={canSendEmail ? undefined : tr('pflege.comm.no_email_hint')}
              style={{
                fontSize: 12, fontWeight: 600, background: BRAND_LIGHT, color: BRAND,
                border: `1px solid ${BRAND}`, borderRadius: 7, padding: '5px 12px',
                cursor: canSendEmail ? 'pointer' : 'not-allowed',
                opacity: canSendEmail ? 1 : 0.5,
              }}
            >+ {tr('pflege.comm.template.button')}</button>
          </div>
        </div>

        {/* send form */}
        {showSendForm && user && (
          <SendMessageForm
            dark={dark}
            user={user}
            accountId={accountId}
            members={members}
            onSent={handleSent}
            onCancel={() => setShowSendForm(false)}
          />
        )}

        {/* email template modal */}
        {showTemplateModal && user && (
          <EmailVorlageModal
            dark={dark}
            user={user}
            onSent={handleTemplateSent}
            onClose={() => setShowTemplateModal(false)}
          />
        )}

        {/* message list */}
        {error ? (
          <div style={{ padding: 20, fontSize: 13, color: '#b42318' }}>{error}</div>
        ) : (
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            {!loading && messages.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: t.muted, fontSize: 13 }}>
                {tr('pflege.comm.empty')}
              </div>
            )}
            {messages.map(m => <MessageRow key={m.id} m={m} dark={dark} />)}
          </div>
        )}
      </Card>

      {/* Chat mit dem Kunden (CRM-1.0-App-Chat: chats-Tabelle, 10s-Polling). */}
      <ChatCard user={user} dark={dark} />

      {/* Gesprächsprotokolle (ehem. Anrufhistorie) mit Transkripten */}
      <Gespraechsprotokolle userId={user?.id} dark={dark} />

    </div>
  );
}

/* ─── Chat mit dem Kunden (CRM-1.0-App-Chat) ───────────────────────────────── */

function ChatCard({ user, dark }: { user: V1User | null; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { user: currentAdmin } = useAuth();
  const [chats, setChats] = useState<V1Chat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const lastTimeRef = useRef<string | null>(null);
  // IDs bereits angezeigter Nachrichten: /updates vergleicht `since`
  // inklusiv (gleichsekündige Nachrichten gehen nicht verloren), liefert die
  // zuletzt bekannte also erneut — sie wird hier herausgefiltert.
  const knownIdsRef = useRef<Set<number>>(new Set());

  const scrollDown = () => {
    window.setTimeout(() => {
      const el = listRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  };

  // Initial laden + als gelesen markieren; danach 10s-Polling über /updates.
  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    v1Chat.list(user.id)
      .then(list => {
        if (!alive) return;
        setChats(list);
        knownIdsRef.current = new Set(list.map(c => c.id));
        lastTimeRef.current = list.length > 0 ? list[list.length - 1].created_at : new Date().toISOString();
        scrollDown();
        void v1Chat.markRead(user.id).catch(() => { /* still */ });
      })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('pflege.comm.error_load')); })
      .finally(() => { if (alive) setLoading(false); });

    const timer = window.setInterval(async () => {
      if (!lastTimeRef.current) return;
      try {
        const fresh = await v1Chat.updates(user.id, lastTimeRef.current);
        const add = fresh.filter(c => !knownIdsRef.current.has(c.id));
        if (!alive || add.length === 0) return;
        add.forEach(c => knownIdsRef.current.add(c.id));
        setChats(prev => {
          const known = new Set(prev.map(c => c.id));
          const missing = add.filter(c => !known.has(c.id));
          return missing.length > 0 ? [...prev, ...missing] : prev;
        });
        lastTimeRef.current = add[add.length - 1].created_at;
        scrollDown();
        void v1Chat.markRead(user.id).catch(() => { /* still */ });
      } catch { /* Poll-Fehler still — nächster Tick versucht es erneut */ }
    }, 10000);

    return () => { alive = false; window.clearInterval(timer); };
  }, [user?.id, tr]);

  // @-Erwähnungen — dieselbe Auswahlliste wie im Team-Chat.
  const chatInputRef = React.useRef<HTMLInputElement>(null);
  const mentions = useMentionInput({ value: draft, onChange: setDraft, inputRef: chatInputRef });

  const send = async () => {
    if (!user?.id || sending || !draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      const created = await v1Chat.send(user.id, draft.trim());
      knownIdsRef.current.add(created.id);
      setChats(prev => [...prev, created]);
      lastTimeRef.current = created.created_at;
      setDraft('');
      mentions.reset();
      scrollDown();
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('pflege.comm.chat_send_error'));
    } finally {
      setSending(false);
    }
  };

  // Kompakt „26.10. 14:30“ (Chat); Ungültiges bleibt roh.
  const fmtTime = (iso: string) => {
    const time = formatTime(iso, '');
    return time ? `${formatDayMonth(iso)} ${time}` : iso;
  };

  return (
    <Card dark={dark} style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>{tr('pflege.comm.chat_title')}</h3>
        <span style={{ fontSize: 11, color: t.muted }}>{tr('pflege.comm.chat_hint')}</span>
      </div>

      <div ref={listRef} style={{ maxHeight: 380, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {loading && <div style={{ fontSize: 12, color: t.muted }}>{tr('common.loading_short')}</div>}
        {!loading && chats.length === 0 && (
          <div style={{ fontSize: 12.5, color: t.muted, fontStyle: 'italic' }}>{tr('pflege.comm.chat_empty')}</div>
        )}
        {chats.map(c => {
          const fromUser = String(c.von) === 'user';
          const own = !fromUser && Number(c.von) === Number(currentAdmin?.id);
          return (
            <div key={c.id} style={{ display: 'flex', justifyContent: fromUser ? 'flex-start' : 'flex-end' }}>
              <div style={{
                maxWidth: '75%', borderRadius: 12, padding: '8px 12px', fontSize: 13,
                background: fromUser ? t.cardMuted : (own ? BRAND : BRAND_LIGHT),
                color: fromUser ? t.text : (own ? '#fff' : BRAND),
                border: `1px solid ${fromUser ? t.borderSoft : 'transparent'}`,
              }}>
                {c.typ === 'text' || !c.datei ? (
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.inhalt}</div>
                ) : (
                  <div style={{ fontStyle: 'italic' }}>📎 {c.datei}</div>
                )}
                <div style={{ fontSize: 10, opacity: 0.7, marginTop: 3, textAlign: 'right' }}>{fmtTime(c.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Eingabe */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: `1px solid ${t.border}`, position: 'relative' }}>
        <MentionPopover dark={dark} {...mentions.popoverProps} />
        <input
          ref={chatInputRef}
          value={draft}
          onChange={mentions.handleInput}
          onSelect={mentions.handleSelect}
          onKeyDown={e => {
            // Enter wählt erst die Erwähnung aus, bevor es sendet.
            if (mentions.handleKeyDown(e)) return;
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); }
          }}
          placeholder={tr('pflege.comm.chat_input_ph')}
          disabled={!user?.id || sending}
          aria-label={tr('pflege.comm.chat_input_ph')}
          style={{
            flex: 1, fontSize: 13, border: `1px solid ${t.border}`, borderRadius: 9,
            padding: '9px 12px', background: t.inputBg, color: t.text, outline: 'none',
            fontFamily: 'Outfit,sans-serif',
          }}
        />
        <button
          onClick={() => void send()}
          disabled={!user?.id || sending || !draft.trim()}
          style={{
            fontSize: 13, fontWeight: 600, background: BRAND, color: '#fff', border: 'none',
            borderRadius: 9, padding: '9px 16px', cursor: sending ? 'wait' : 'pointer',
            opacity: (!draft.trim() || sending) ? 0.6 : 1, fontFamily: 'Outfit,sans-serif',
          }}
        >
          {sending ? '…' : tr('pflege.comm.chat_send')}
        </button>
      </div>
      {error && <div style={{ padding: '0 16px 12px', fontSize: 12, color: '#b42318' }}>{error}</div>}
    </Card>
  );
}

/* ─── MessageRow ────────────────────────────────────────────────────────────── */

function MessageRow({ m, dark }: { m: MessageRowData; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const dateLabel = formatDateTime(m.created_at);
  const betreff = (m as { betreff?: string }).betreff ?? tr('pflege.comm.no_subject');
  const status = (m as { status?: string | number }).status;
  const statusLabel = status === 1 || status === '1' ? tr('pflege.comm.status.confirmed') : tr('pflege.comm.status.sent');
  const statusBg = status === 1 || status === '1'
    ? (dark ? '#0f3a23' : '#ecfdf3')
    : (dark ? '#1a2a3a' : '#eff8ff');
  const statusFg = status === 1 || status === '1'
    ? (dark ? '#86efac' : '#027a48')
    : (dark ? '#7dd3fc' : '#026aa2');
  return (
    <div style={{
      padding: '12px 20px',
      borderBottom: `1px solid ${t.borderSoft}`,
      display: 'grid',
      gridTemplateColumns: '110px 1fr auto',
      alignItems: 'center', gap: 14,
    }}>
      <span style={{ fontSize: 12, color: t.muted, fontFamily: 'monospace' }}>{dateLabel}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, color: t.heading,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{betreff}</div>
        {/* Batch-13 Paket E — Personen-Chip: nur im Konto-Modus vorhanden. */}
        {m.user_name && (
          <div style={{ marginTop: 3, fontSize: 11, color: t.muted }}>
            <span style={{ background: t.hover, border: `1px solid ${t.border}`, padding: '2px 8px', borderRadius: 999 }}>
              {tr('pflege.tasks.person_chip', { name: m.user_name })}
            </span>
          </div>
        )}
      </div>
      <span style={{
        fontSize: 11, fontWeight: 600,
        background: statusBg, color: statusFg,
        borderRadius: 999, padding: '3px 10px',
      }}>{statusLabel}</span>
    </div>
  );
}
